import {
  Home,
  Business,
  Settings,
  SwapHoriz,
  Tablet,
  Warehouse,
} from "@mui/icons-material";
import { green, grey, indigo, orange, lightBlue } from "@mui/material/colors";
import { ReactNode } from "react";
import { useMatch } from "react-router-dom";
import { Theme, useTheme } from "@mui/material";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import { useCurrentUser } from "../../../hooks/react-query/settings/security/useUsers";
import { useMySegments } from "../../../hooks/react-query/splitIoApi/useMySegments";

export const HOME_URL_KEY = "/";
export const BACK_OFFICE_URL_KEY = "back-office";
export const OPERATIONS_URL_KEY = "operations";
export const LINEHAUL_URL_KEY = "linehaul";
export const DOCK_AUTOMATION_URL_KEY = "dock-automation";
export const SETTINGS_URL_KEY = "settings";

export type DepartmentUrlKey =
  | typeof BACK_OFFICE_URL_KEY
  | typeof OPERATIONS_URL_KEY
  | typeof LINEHAUL_URL_KEY
  | typeof DOCK_AUTOMATION_URL_KEY
  | typeof SETTINGS_URL_KEY
  | typeof HOME_URL_KEY;

export type DepartmentChildRoute = {
  label: string;
  route: string;
  enabled: boolean;
};

export type Department = {
  urlKey: DepartmentUrlKey;
  label: string;
  icon: ReactNode;
  active: boolean;
  accentColor: string;
  childRoutes: DepartmentChildRoute[];
  enabled: boolean;
  showServiceCenterSelect: boolean;
};

export function useDepartments(): Department[] {
  const theme = useTheme();
  const { data: user } = useCurrentUser();
  const { data: mySegments = [] } = useMySegments(user?.id ?? "");

  const home = useHome(theme);
  const backOffice = useBackOffice(theme);
  const operations = useOperations(theme);
  const linehaul = useLinehaul(theme);
  const dockAutomation = useDockAutomation(theme);
  const settings = useSettings(theme);

  if (!user) return [];

  return [
    // If the user is only assigned to one segment, that means they only use one department,
    // so we hide home and redirect them directly to the single department.
    ...(mySegments.length === 1 ? [] : [home]),
    backOffice,
    dockAutomation,
    linehaul,
    operations,
    settings,
  ];
}

function useBackOffice(theme: Theme): Department {
  const backOfficeEnabled = useIsFeatureFlagEnabled("back-office-client");
  const backOfficeNavigationHomeEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-198-navigation-home-client"
  );
  const backOfficeNavigationBillingEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-199-navigation-billing-client"
  );
  const backOfficeNavigationPricingRatingEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-201-navigation-pricing-rating-client"
  );
  const backOfficeNavigationSalesEnabled = useIsFeatureFlagEnabled(
    "back-office-bod-202-navigation-sales-client"
  );

  return {
    urlKey: BACK_OFFICE_URL_KEY,
    label: "BackOffice",
    icon: (
      <Business
        fontSize="inherit"
        sx={{ color: theme.palette.primary.contrastText }}
      />
    ),
    active: !!useMatch("/" + BACK_OFFICE_URL_KEY + "/*"),
    accentColor: green["700"],
    childRoutes: [
      {
        label: "Home",
        route: "back-office/home",
        enabled: backOfficeNavigationHomeEnabled,
      },
      {
        label: "Billing",
        route: "back-office/billing",
        enabled: backOfficeNavigationBillingEnabled,
      },
      {
        label: "Clients",
        route: "back-office/clients",
        enabled: true,
      },
      {
        label: "Pricing & Rating",
        route: "back-office/pricing-and-rating",
        enabled: backOfficeNavigationPricingRatingEnabled,
      },
      {
        label: "Sales",
        route: "back-office/sales",
        enabled: backOfficeNavigationSalesEnabled,
      },
    ],
    enabled: backOfficeEnabled,
    showServiceCenterSelect: false,
  };
}

function useOperations(theme: Theme): Department {
  const operationsEnabled = useIsFeatureFlagEnabled("operations-client");

  const operationsInboundEnabled = useIsFeatureFlagEnabled(
    "operations-inbound-client"
  );

  const operationsOutboundEnabled = useIsFeatureFlagEnabled(
    "operations-outbound-client"
  );

  const operationsEquipmentInventoryEnabled = useIsFeatureFlagEnabled(
    "inbound-3876-equipment-inventory-layout-client"
  );

  const operationsDockAutomationEnabled = useIsFeatureFlagEnabled(
    "operation-1733-dock-automation-client"
  );

  const dispatchEnabled = useIsFeatureFlagEnabled("dispatch-client");

  return {
    urlKey: OPERATIONS_URL_KEY,
    label: "Operations",
    icon: (
      <Warehouse
        fontSize="inherit"
        sx={{ color: theme.palette.primary.contrastText }}
      />
    ),
    active: !!useMatch("/" + OPERATIONS_URL_KEY + "/*"),
    accentColor: orange["500"],
    childRoutes: [
      {
        label: "Inbound",
        route: "operations/:serviceCenterCode/inbound",
        enabled: operationsInboundEnabled,
      },
      {
        label: "Dispatch",
        route: "operations/:serviceCenterCode/dispatch",
        enabled: dispatchEnabled,
      },
      {
        label: "Outbound",
        route: "operations/:serviceCenterCode/outbound",
        enabled: operationsOutboundEnabled,
      },
      {
        label: "Equipment Inventory",
        route: "operations/:serviceCenterCode/equipment-inventory",
        enabled: operationsEquipmentInventoryEnabled,
      },
      {
        label: "Dock Automation",
        route: "operations/:serviceCenterCode/dock-automation",
        enabled: operationsDockAutomationEnabled,
      },
    ],
    enabled: operationsEnabled,
    showServiceCenterSelect: true,
  };
}

function useLinehaul(theme: Theme): Department {
  const linehaulEnabled = useIsFeatureFlagEnabled("linehaul-client");

  return {
    urlKey: LINEHAUL_URL_KEY,
    label: "Linehaul",
    icon: (
      <SwapHoriz
        fontSize="inherit"
        sx={{ color: theme.palette.primary.contrastText }}
      />
    ),
    active: !!useMatch("/" + LINEHAUL_URL_KEY + "/*"),
    accentColor: indigo["600"],
    childRoutes: [
      {
        label: "Master Schedule",
        route: "linehaul/master-schedule",
        enabled: true,
      },
      {
        label: "Templates",
        route: "linehaul/master-schedule-templates",
        enabled: true,
      },
      {
        label: "Load Board",
        route: "linehaul/load-board-v1",
        enabled: false,
      },
      {
        label: "Load Board",
        route: "linehaul/load-board",
        enabled: true,
      },
      {
        label: "Trips Dashboard",
        route: "linehaul/trips-dashboard",
        enabled: true,
      },
    ],
    enabled: linehaulEnabled,
    showServiceCenterSelect: false,
  };
}

function useDockAutomation(theme: Theme): Department {
  return {
    urlKey: DOCK_AUTOMATION_URL_KEY,
    label: "Dock Auto",
    icon: (
      <Tablet
        fontSize="inherit"
        sx={{ color: theme.palette.primary.contrastText }}
      />
    ),
    active: !!useMatch("/" + DOCK_AUTOMATION_URL_KEY + "/*"),
    accentColor: green["400"],
    childRoutes: [],
    enabled: false,
    showServiceCenterSelect: false,
  };
}

function useSettings(theme: Theme): Department {
  return {
    urlKey: SETTINGS_URL_KEY,
    label: "Settings",
    icon: (
      <Settings
        fontSize="inherit"
        sx={{ color: theme.palette.primary.contrastText }}
      />
    ),
    active: !!useMatch("/" + SETTINGS_URL_KEY + "/*"),
    accentColor: grey["600"],
    childRoutes: [],
    enabled: true,
    showServiceCenterSelect: true,
  };
}

function useHome(theme: Theme): Department {
  return {
    urlKey: HOME_URL_KEY,
    label: "Home",
    icon: (
      <Home
        fontSize="inherit"
        sx={{ color: theme.palette.primary.contrastText }}
      />
    ),
    active: !!useMatch(HOME_URL_KEY),
    accentColor: lightBlue["700"],
    childRoutes: [],
    enabled: true,
    showServiceCenterSelect: false,
  };
}
